import { type PropsWithChildren } from 'react';

import { useId } from '../../../../core/hooks/useId.js';
import { DynamicTooltipRenderer } from './components/DynamicTooltipRenderer.js';
import { Footer } from './components/Footer.js';
import { Action } from './components/global-actions/Action.js';
import { ActionsPlaceholder } from './components/global-actions/ActionsPlaceholder.js';
import { Header } from './components/header/Header.js';
import { Series } from './components/series/Series.js';
import { SeriesHeader } from './components/series/SeriesHeader.js';
import { SeriesItem } from './components/series/SeriesItem.js';
import { TooltipPortal } from './components/TooltipPortal.js';
import { ActiveSeriesMenuProvider } from './contexts/ActiveSeriesMenuContext.js';
import { useOverlay } from './contexts/OverlayContext.js';
import { useCanvasAriaDescribedBy } from './hooks/useCanvasAriaDescribedBy.js';
import { useSvgAriaDescribedBy } from './hooks/useSvgAriaDescribedBy.js';
import type { OverlayTooltipProps } from './types.js';

/**
 * Tooltip root component
 * @internal
 */
const TooltipRoot = (props: PropsWithChildren<OverlayTooltipProps>) => {
  const tooltipId = useId();
  const overlay = useOverlay();
  const isPortalMode =
    props.absoluteX !== undefined && props.absoluteY !== undefined;

  useCanvasAriaDescribedBy(overlay.overlayRef, tooltipId, isPortalMode);
  useSvgAriaDescribedBy(overlay.overlayRef, tooltipId, isPortalMode);

  if (isPortalMode) {
    return (
      <ActiveSeriesMenuProvider>
        <TooltipPortal
          id={tooltipId}
          isInteractive={props.isInteractive ?? false}
          absoluteX={props.absoluteX}
          absoluteY={props.absoluteY}
        >
          {props.children}
        </TooltipPortal>
      </ActiveSeriesMenuProvider>
    );
  }

  return <DynamicTooltipRenderer {...props} />;
};

/**
 * OverlayTooltip WIP component
 * @internal
 */
export const OverlayTooltip = Object.assign(TooltipRoot, {
  Action,
  ActionsPlaceholder,
  Footer,
  Header,
  Series,
  SeriesHeader,
  SeriesItem,
});

export const Tooltip = () => null;
Tooltip.displayName = 'ChartTooltip';
/**
 * ChartTooltip Component
 * @public
 */
export const ChartTooltip = Object.assign(Tooltip, {
  Action,
  ActionsPlaceholder,
  Footer,
  Header,
  Series,
  SeriesHeader,
  SeriesItem,
});
